"use client";

import { useTranslation } from "react-i18next";
import type { ApiPiece } from "@/hooks/use-projects";
import { Card } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/status-badge";
import { AiScoreBadge } from "@/components/ui/ai-score-badge";
import { PlatformIcon, getPlatformLabel } from "@/components/ui/platform-icon";
import { POST_TYPE_META, type PostType } from "@/hooks/use-post-generation";
import { MediaPreview, mediaItemFromPiece } from "@/components/media";
import { Button } from "@/components/ui/button";
import { Check, X, AlertTriangle, Loader2, Image as ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface GalleryCardProps {
  piece: ApiPiece;
  onClick: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  isSelected?: boolean;
  canApprove?: boolean;
}

export function GalleryCard({ piece, onClick, onApprove, onReject, isSelected, canApprove }: GalleryCardProps) {
  const { t } = useTranslation("pieces");
  const isTranscoding = !!piece.transcodeJobId;
  const mi = mediaItemFromPiece({
    id: piece.id,
    mediaUrls: piece.mediaUrls,
    thumbnailUrl: piece.thumbnailUrl,
    format: piece.format,
    title: piece.shortCode ?? piece.caption,
  });
  return (
    <Card
      className={cn(
        "cursor-pointer overflow-hidden transition-all hover:ring-1 hover:ring-foreground/20",
        isSelected && "ring-2 ring-primary",
      )}
      onClick={onClick}
    >
      <div className="relative">
        {mi ? (
          <MediaPreview item={mi} className="aspect-square" />
        ) : (
          <div className="flex aspect-square items-center justify-center bg-muted">
            <ImageIcon size={24} className="text-muted-foreground" />
          </div>
        )}
        <div className="absolute left-2 top-2">
          <PlatformIcon platform={piece.platform} size={16} />
        </div>
        {piece.requiresReapproval && (
          <div className="absolute right-2 top-2">
            <AlertTriangle size={14} className="text-amber-400" />
          </div>
        )}
        {isTranscoding && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[1px]">
            <div className="flex items-center gap-1.5 rounded-md bg-background/90 px-2.5 py-1 text-xs">
              <Loader2 size={12} className="animate-spin" />
              {t("pieces:transcoding")}
            </div>
          </div>
        )}
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="font-mono text-xs text-muted-foreground">{piece.shortCode}</span>
          <div className="flex items-center gap-1">
            <AiScoreBadge score={piece.aiReviewScore} className="text-[10px]" />
            <StatusBadge status={piece.status} className="text-[10px]" />
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          {piece.postType && POST_TYPE_META[piece.postType as PostType]
            ? `${getPlatformLabel(POST_TYPE_META[piece.postType as PostType].platform)} · ${POST_TYPE_META[piece.postType as PostType].label}`
            : `${getPlatformLabel(piece.platform)} · ${piece.format.toLowerCase()}`}
        </div>
        <p className="text-sm line-clamp-2">{piece.copy || piece.caption}</p>
        {piece.status === "PENDING_REVIEW" && canApprove && !isTranscoding && (
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              className="h-7 flex-1 text-green-400 border-green-500/20 hover:bg-green-500/10"
              onClick={(e) => { e.stopPropagation(); onApprove?.(); }}
            >
              <Check size={12} className="mr-1" /> {t("pieces:actions.approve")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 flex-1 text-red-400 border-red-500/20 hover:bg-red-500/10"
              onClick={(e) => { e.stopPropagation(); onReject?.(); }}
            >
              <X size={12} className="mr-1" /> {t("pieces:actions.reject")}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
